// frontend/src/components/company/CompanyList.tsx

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { CompanyCard } from './CompanyCard'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'
import { Spinner } from '@/components/common/UI/Spinner'
import { useAuthStore } from '@/store'
import { useDebounce } from '@/hooks/useDebounce'

// ============================================
// Компонент списка компаний
// ============================================

interface CompanyListProps {
  title?: string
  limit?: number
  showCreate?: boolean
  showSearch?: boolean
  filterByOwner?: boolean
  onCompanyClick?: (company: any) => void
}

export const CompanyList: React.FC<CompanyListProps> = ({
  title = 'Компании',
  limit,
  showCreate = true,
  showSearch = true,
  filterByOwner = false,
  onCompanyClick
}) => {
  const { user, isAuthenticated } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(searchQuery, 500)

  const isEmployer = user?.role === 'employer' || user?.role === 'admin'

  // Получение компаний
  const { data, isLoading, error } = useQuery({
    queryKey: ['companies', debouncedSearch, page, filterByOwner],
    queryFn: async () => {
      const params: any = { page, limit: limit || 20 }

      if (debouncedSearch) {
        params.search = debouncedSearch
      }

      if (filterByOwner && user) {
        // Получаем компании пользователя (владельца)
        return apiClient.get('/v1/companies/my', { params })
      }

      return apiClient.get('/v1/companies', { params })
    },
    enabled: !filterByOwner || !!user
  })

  const companies = data?.items || []
  const total = data?.total || 0
  const hasMore = limit ? companies.length < total : companies.length < total

  const handleLoadMore = () => {
    setPage(page + 1)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setPage(1)
  }

  if (isLoading && page === 1) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-error">Ошибка загрузки компаний</p>
        <Button variant="outline" className="mt-2" onClick={() => window.location.reload()}>
          Попробовать снова
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-secondary-900">{title}</h2>
          {total > 0 && (
            <p className="text-sm text-secondary-500">Найдено: {total} компаний</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {showSearch && (
            <Input
              placeholder="Поиск компаний..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-48 sm:w-64"
              leftIcon={
                <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              }
            />
          )}

          {showCreate && isAuthenticated && isEmployer && (
            <Button size="sm" onClick={() => window.location.href = '/companies/create'}>
              + Создать
            </Button>
          )}
        </div>
      </div>

      {/* Список компаний */}
      {companies.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((company: any) => (
            <div
              key={company.id}
              onClick={() => onCompanyClick?.(company)}
              className={onCompanyClick ? 'cursor-pointer' : ''}
            >
              <CompanyCard company={company} />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-secondary-50 rounded-lg">
          <svg className="w-16 h-16 mx-auto text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="mt-4 text-secondary-500">Компаний не найдено</p>
          {showCreate && isAuthenticated && isEmployer && (
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => window.location.href = '/companies/create'}
            >
              Создать компанию
            </Button>
          )}
        </div>
      )}

      {/* Кнопка "Загрузить еще" */}
      {hasMore && (
        <div className="text-center pt-4">
          <Button variant="outline" onClick={handleLoadMore} isLoading={isLoading}>
            Загрузить еще
          </Button>
        </div>
      )}

      {/* Статистика */}
      {total > 0 && (
        <div className="text-center text-sm text-secondary-400">
          Показано {companies.length} из {total} компаний
        </div>
      )}
    </div>
  )
}

// ============================================
// Компонент списка компаний с пагинацией
// ============================================

interface CompanyListPaginatedProps {
  title?: string
  showCreate?: boolean
  showSearch?: boolean
  filterByOwner?: boolean
  onCompanyClick?: (company: any) => void
}

export const CompanyListPaginated: React.FC<CompanyListPaginatedProps> = (props) => {
  return (
    <div className="space-y-6">
      <CompanyList {...props} />
    </div>
  )
}

// ============================================
// Компонент компаний пользователя (для дашборда)
// ============================================

export const MyCompanies: React.FC = () => {
  const { user } = useAuthStore()
  const isEmployer = user?.role === 'employer' || user?.role === 'admin'

  if (!isEmployer) {
    return (
      <div className="text-center py-8">
        <p className="text-secondary-500">Только для работодателей</p>
      </div>
    )
  }

  return (
    <CompanyList
      title="Мои компании"
      filterByOwner
      showCreate
      showSearch
    />
  )
}

// ============================================
// Компонент выбора компании (для форм)
// ============================================

interface CompanySelectProps {
  value?: number
  onChange: (companyId: number) => void
  placeholder?: string
}

export const CompanySelect: React.FC<CompanySelectProps> = ({
  value,
  onChange,
  placeholder = 'Выберите компанию'
}) => {
  const { user } = useAuthStore()

  const { data, isLoading } = useQuery({
    queryKey: ['companies', 'my'],
    queryFn: async () => {
      return apiClient.get('/v1/companies/my')
    },
    enabled: !!user
  })

  const companies = data?.items || []

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner size="sm" />
        <span className="text-sm text-secondary-400">Загрузка компаний...</span>
      </div>
    )
  }

  if (companies.length === 0) {
    return (
      <div className="text-sm text-secondary-500">
        У вас нет компаний. <Link to="/companies/create" className="text-primary-600 hover:underline">Создать компанию</Link>
      </div>
    )
  }

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2.5 text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
    >
      <option value="">{placeholder}</option>
      {companies.map((company: any) => (
        <option key={company.id} value={company.id}>
          {company.name}
        </option>
      ))}
    </select>
  )
}

// ============================================
// Экспорт
// ============================================

export default CompanyList